import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { PackagingPOActionsCell } from 'components/packaging/components/packagingPOV2/PackagingPOActionsCell';
import { PackagingPOStatusCell } from 'components/packaging/components/packagingPOV2/PackagingPOStatusCell';
import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { ResultsCounter } from 'shared';

const FALLBACK_TEXT = '—';

const renderStatusCell = ({ getValue }) => <PackagingPOStatusCell status={getValue()} fallbackText={FALLBACK_TEXT} />;

const renderActionsCell = ({ row, table }) => {
    const { onCancelPurchaseOrder, onDownloadPdf, onDownloadShipList, onDuplicatePurchaseOrder, onViewPurchaseOrder } =
        table.options.meta ?? {};
    const isSendToVendorDisabled = table.options.meta?.isSendToVendorDisabled;
    const onSendToVendor = table.options.meta?.onSendToVendor;
    return (
        <PackagingPOActionsCell
            onViewClick={() => onViewPurchaseOrder?.(row.original)}
            onCancel={() => onCancelPurchaseOrder?.(row.original)}
            onDownloadPdf={() => onDownloadPdf?.(row.original)}
            onDownloadShipList={() => onDownloadShipList?.(row.original)}
            onDuplicate={() => onDuplicatePurchaseOrder?.(row.original)}
            onSendToVendor={() => onSendToVendor?.(row.original)}
            isSendToVendorDisabled={isSendToVendorDisabled?.(row.original)}
        />
    );
};

/**
 * Memoized row component to prevent unnecessary re-renders.
 */
const MemoizedPackagingPORow = React.memo(({ row }) => (
    <TableRow data-state={row.getIsSelected() ? 'selected' : undefined}>
        {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

/**
 * Renders a v2-styled table for Packaging Purchase Orders.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} [props.data=[]] - Packaging PO rows.
 * @param {boolean} [props.isLoading=false] - Initial loading state.
 * @param {boolean} [props.isFetching=false] - Whether any fetch is in progress.
 * @param {string} [props.emptyMessage='No packaging purchase orders found.'] - Empty state message.
 * @returns {JSX.Element} Packaging PO table.
 */
export function PackagingPOTable({
    data = [],
    emptyMessage = 'No packaging purchase orders found.',
    isFetching = false,
    isLoading = false,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(
        () => [
            {
                header: 'PO Number',
                accessorKey: 'id',
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: renderStatusCell,
            },
            {
                header: 'Vendor',
                accessorKey: 'vendorName',
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'Date',
                accessorKey: 'scheduledDeliveryDate',
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue(), FALLBACK_TEXT),
            },
            {
                header: 'FC/Delivery Location',
                accessorKey: 'facilityName',
                cell: ({ row }) =>
                    row.original?.facilityName ??
                    row.original?.deliveryLocation ??
                    row.original?.deliveryLocationName ??
                    FALLBACK_TEXT,
            },
            {
                header: 'Reference #',
                accessorKey: 'referenceNumber',
                cell: ({ row }) => row.original?.referenceNumber ?? row.original?.reference ?? FALLBACK_TEXT,
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: renderActionsCell,
            },
        ],
        [],
    );

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        meta: {},
    });

    const loadedCount = tableData.length;
    const totalCount = tableData.length;
    const isRefreshing = isFetching;

    return (
        <div className="flex flex-1 flex-col gap-2">
            {!isLoading && totalCount > 0 && (
                <div className="px-4 py-2">
                    <ResultsCounter loadedCount={loadedCount} totalCount={totalCount} isRefreshing={isRefreshing} />
                </div>
            )}

            <div className="flex min-h-[320px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading purchase orders…</span>
                        </div>
                    </div>
                ) : (
                    <div className="pb-6">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id} className="whitespace-normal break-words">
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => <MemoizedPackagingPORow key={row.id} row={row} />)
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}

PackagingPOTable.displayName = 'PackagingPOTable';
